import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { MdOutlineDeleteForever } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import {
  deleteCqaStaffData,
  updateCqaStaffData,
  fetchCqaStaffData,
} from "../redux/actions/CQAAction";
import {
  getCqaStaffPriorityData,
  updateCqaStaffPriority,
} from "../controllers/cqaStaffTable.controller";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }],
    ["bold", "italic", "underline"],
    [{ align: [] }],
    ["clean"],
  ],
};

const formats = ["font", "header", "bold", "italic", "underline", "align"];

export default function CqaStaffTable({
  title = "CQA Staff List",
  MemberData = [],
  loading,
}) {
  const dispatch = useDispatch();

  const [priorityData, setPriorityData] = useState([]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchPriorityData = async () => {
    try {
      const res = await getCqaStaffPriorityData();
      if (res.status === 200) {
        setPriorityData(res?.data?.numbers || []);
      }
    } catch (error) {
      console.error("Error fetching CQA staff priority data:", error);
    }
  };

  useEffect(() => {
    fetchPriorityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MemberData.length]);

  const handlePriorityChange = async (id, newOrder) => {
    try {
      toast.loading("Updating priority...");
      const response = await updateCqaStaffPriority({ id, order: newOrder });
      toast.dismiss();
      if (response.status === 200) {
        toast.success("Priority updated successfully!");
        dispatch(fetchCqaStaffData());
      } else {
        toast.error("Failed to update priority.");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.message || "Something went wrong!"
      );
    }
  };

  const handleDelete = (staff) => {
    setSelectedStaff(staff);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCloseModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedStaff(null);
  };

  const confirmDelete = async () => {
    if (!selectedStaff) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteCqaStaffData(selectedStaff.id));
    } catch (error) {
      alert("Failed to remove CQA staff member.");
    } finally {
      setIsDeleting(false);
      handleDeleteCloseModal();
    }
  };

  const handleEdit = (staff) => {
    setEditStaff({ ...staff });
    setEditImagePreview(null);
    setEditFile(null);
    setIsEditModalOpen(true);
  };

  const handleEditCloseModal = () => {
    setIsEditModalOpen(false);
    setEditStaff(null);
    setEditImagePreview(null);
    setEditFile(null);
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setEditImagePreview(reader.result);
      reader.readAsDataURL(file);
      setEditFile(file);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editStaff?.name?.trim() || !editStaff?.designation?.trim()) {
      alert("Name and designation are required.");
      return;
    }

    const formPayload = new FormData();
    formPayload.append("name", editStaff.name);
    formPayload.append("designation", editStaff.designation);
    formPayload.append("qualifications", editStaff.qualifications || "");
    formPayload.append("description", editStaff.description || "");
    if (editFile) formPayload.append("file", editFile);

    setIsUpdating(true);
    try {
      await dispatch(updateCqaStaffData(editStaff.id, formPayload));
      alert("CQA staff member updated successfully!");
      dispatch(fetchCqaStaffData());
      handleEditCloseModal();
    } catch (error) {
      alert(
        `Error updating CQA staff: ${
          error.response?.data?.message || "Something went wrong."
        }`
      );
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-4 mt-6 bg-white border border-gray-300 rounded-lg shadow-md">
      <h2 className="mb-4 text-xl font-semibold text-gray-700">{title}</h2>
      <table className="w-full border border-gray-300 table-auto">
        <thead>
          <tr className="text-left text-gray-600 bg-gray-100">
            <th className="px-4 py-2">PROFILE</th>
            <th className="px-4 py-2">NAME</th>
            <th className="px-4 py-2">DESIGNATION</th>
            <th className="px-4 py-2">PRIORITY</th>
            <th className="px-4 py-2">ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {MemberData.length === 0 ? (
            <tr>
              <td
                colSpan="5"
                className="px-4 py-4 font-medium text-center text-gray-600"
              >
                No CQA staff added yet.
              </td>
            </tr>
          ) : (
            MemberData.map((staff) => (
              <tr key={staff.id} className="border-b border-gray-300">
                <td className="px-4 py-2">
                  <img
                    src={staff.image_url || staff.image}
                    alt={staff.name}
                    className="object-cover w-12 h-12 rounded"
                  />
                </td>
                <td className="px-4 py-2">{staff.name}</td>
                <td className="px-4 py-2">{staff.designation}</td>
                <td className="px-4 py-2 text-center">
                  <select
                    value={staff.cqa_order}
                    onChange={(e) =>
                      handlePriorityChange(staff.id, e.target.value)
                    }
                    className="px-2 py-1 border border-gray-300 rounded-md"
                  >
                    {priorityData.map((priority) => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      className="flex items-center px-5 py-1 text-gray-400 bg-gray-100 border border-gray-400 rounded"
                      onClick={() => handleDelete(staff)}
                    >
                      Delete
                      <MdOutlineDeleteForever className="w-5 h-5 ml-2" />
                    </button>
                    <button
                      type="button"
                      className="flex items-center px-5 py-1 border rounded text-primary-color border-primary-color bg-primary-color bg-opacity-10"
                      onClick={() => handleEdit(staff)}
                    >
                      Edit
                      <FaRegEdit className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {isDeleteModalOpen && selectedStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[400px] text-center">
            <h3 className="mb-4 text-xl font-semibold text-gray-700">Remove?</h3>
            <p className="mb-6 text-gray-600">
              Are you sure you want to remove this staff member?
              <br />
              <strong>{selectedStaff.name}</strong>
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="px-6 py-2 text-gray-500 bg-gray-200 rounded-md hover:bg-gray-300"
                onClick={handleDeleteCloseModal}
              >
                No
              </button>
              <button
                className={`px-6 py-2 text-white rounded-md ${
                  isDeleting ? "bg-gray-400 cursor-not-allowed" : "bg-[#92288C]"
                }`}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Removing..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && editStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-1/2 p-6 bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <h2 className="mb-4 text-xl font-bold">Edit CQA Staff</h2>
            <form onSubmit={handleSaveEdit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-600">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editStaff.name}
                    onChange={(e) =>
                      setEditStaff({ ...editStaff, name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-600">
                    Designation
                  </label>
                  <input
                    type="text"
                    value={editStaff.designation}
                    onChange={(e) =>
                      setEditStaff({ ...editStaff, designation: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-600">
                  Qualifications
                </label>
                <ReactQuill
                  value={editStaff.qualifications || ""}
                  onChange={(value) =>
                    setEditStaff({ ...editStaff, qualifications: value })
                  }
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  className="h-[160px] mb-12"
                />
              </div>

              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-600">
                  Description <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={editStaff.description || ""}
                  onChange={(e) =>
                    setEditStaff({ ...editStaff, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                />
              </div>

              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-600">
                  Profile Photo{" "}
                  <span className="text-gray-400">(leave blank to keep current)</span>
                </label>
                <div className="flex items-center gap-4">
                  <img
                    src={editImagePreview || editStaff.image_url || editStaff.image}
                    alt={editStaff.name}
                    className="object-cover w-16 h-16 rounded-full"
                  />
                  <input type="file" accept="image/*" onChange={handleEditFileChange} />
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleEditCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  className={`px-4 py-2 text-white rounded-md ${
                    isUpdating
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#92288C] hover:bg-[#6C0B67]"
                  }`}
                >
                  {isUpdating ? "Updating..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
